<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import ConversationApi from 'dashboard/api/conversations';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  inboxName: { type: String, default: '' },
  disabledReason: { type: String, default: '' },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['select']);
const { t } = useI18n();
const { accountId } = useAccount();
const { run, abort, isPending } = useAbortableRequest();
const popover = ref(null);
const conversations = ref([]);
const deals = ref([]);
const dealId = ref(null);
const nextBeforeId = ref(null);
const error = ref(false);
const clear = () => {
  conversations.value = [];
  deals.value = [];
  dealId.value = null;
  nextBeforeId.value = null;
  error.value = false;
};
const load = async (more = false) => {
  const beforeId = more ? nextBeforeId.value : null;
  if (!more) clear();
  error.value = false;
  await run(async signal => {
    try {
      const { data } = await ConversationApi.getChannelOptions(
        props.conversationId,
        beforeId ? { before_id: beforeId } : {},
        { signal }
      );
      if (signal.aborted) return;
      conversations.value = more
        ? [...conversations.value, ...data.conversations]
        : data.conversations;
      deals.value = data.deals;
      if (!deals.value.some(deal => deal.id === dealId.value)) {
        dealId.value = deals.value.length === 1 ? deals.value[0].id : null;
      }
      nextBeforeId.value = data.next_before_id;
    } catch (failure) {
      if (signal.aborted) return;
      if ([401, 403, 404].includes(failure.response?.status)) clear();
      error.value = true;
    }
  });
};
watch([accountId, () => props.conversationId], () => {
  abort();
  clear();
  popover.value?.hide();
});
const select = conversation => {
  if (
    props.busy ||
    props.disabledReason ||
    conversation.unavailable_reason ||
    (deals.value.length && !dealId.value)
  )
    return;
  emit('select', { conversation, dealId: dealId.value });
};
defineExpose({ hide: () => popover.value?.hide() });
</script>

<template>
  <div class="flex items-center border-t border-n-weak px-3 py-1">
    <Popover ref="popover" align="start" @show="load()" @hide="abort()">
      <template #default="{ isOpen }">
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-repeat-2"
          :label="
            t('CONVERSATION.CHANNEL_SWITCH.TRIGGER', { channel: inboxName })
          "
          :aria-expanded="isOpen"
          aria-haspopup="dialog"
        />
      </template>
      <template #content>
        <section
          role="dialog"
          :aria-label="t('CONVERSATION.CHANNEL_SWITCH.TITLE')"
          class="flex w-80 max-w-full flex-col gap-3 p-4"
        >
          <h3 class="m-0 text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.CHANNEL_SWITCH.TITLE') }}
          </h3>
          <p class="m-0 text-xs text-n-slate-11">
            {{ t('CONVERSATION.CHANNEL_SWITCH.DESCRIPTION') }}
          </p>
          <p
            v-if="disabledReason"
            role="status"
            class="m-0 text-xs text-n-amber-11"
          >
            {{ disabledReason }}
          </p>
          <label v-if="deals.length" class="flex flex-col gap-1 text-xs">
            {{ t('CONVERSATION.CHANNEL_SWITCH.DEAL') }}
            <select v-model="dealId" :disabled="busy" class="mb-0 text-sm">
              <option :value="null" disabled>
                {{ t('CONVERSATION.CHANNEL_SWITCH.SELECT_DEAL') }}
              </option>
              <option v-for="deal in deals" :key="deal.id" :value="deal.id">
                {{ deal.title || deal.product_name || `#${deal.id}` }} ·
                {{ deal.funnel_name }}
              </option>
            </select>
          </label>
          <ul
            class="m-0 flex max-h-64 list-none flex-col divide-y divide-n-weak overflow-y-auto p-0"
          >
            <li
              v-for="conversation in conversations"
              :key="conversation.id"
              class="py-2"
            >
              <button
                type="button"
                class="flex w-full flex-col gap-1 rounded px-1 py-1 text-start hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="
                  busy ||
                  !!disabledReason ||
                  !!conversation.unavailable_reason ||
                  (!!deals.length && !dealId)
                "
                @click="select(conversation)"
              >
                <span class="text-sm font-medium text-n-slate-12">
                  {{ conversation.inbox_name }} · #{{ conversation.id }}
                  <span v-if="conversation.phone_number">
                    · {{ conversation.phone_number }}
                  </span>
                </span>
                <span class="text-xs text-n-slate-11">
                  {{
                    t(
                      `CONVERSATION.CHANNEL_SWITCH.STATUS.${conversation.status}`
                    )
                  }}
                  ·
                  {{
                    conversation.assignee?.name ||
                    t('CONVERSATION.CHANNEL_SWITCH.UNASSIGNED')
                  }}
                </span>
                <span
                  v-if="conversation.unavailable_reason"
                  class="text-xs text-n-amber-11"
                >
                  {{
                    t(
                      `CONVERSATION.CHANNEL_SWITCH.REASONS.${conversation.unavailable_reason}`
                    )
                  }}
                </span>
              </button>
            </li>
          </ul>
          <Spinner v-if="isPending || busy" :size="16" class="self-center" />
          <p v-else-if="error" role="alert" class="m-0 text-xs text-n-ruby-11">
            {{ t('CONVERSATION.CHANNEL_SWITCH.ERROR') }}
          </p>
          <p
            v-else-if="!conversations.length"
            role="status"
            class="m-0 text-xs text-n-slate-11"
          >
            {{ t('CONVERSATION.CHANNEL_SWITCH.EMPTY') }}
          </p>
          <Button
            v-if="nextBeforeId || error"
            variant="ghost"
            size="sm"
            :disabled="isPending || busy"
            :label="
              t(
                error
                  ? 'CONVERSATION.CHANNEL_SWITCH.RETRY'
                  : 'CONVERSATION.CHANNEL_SWITCH.MORE'
              )
            "
            @click="load(!!nextBeforeId)"
          />
        </section>
      </template>
    </Popover>
  </div>
</template>
